<template lang="pug">
.chart
  .chart-loader(v-if="loading")
    q-spinner(color="primary", size="3em")
  .chart-wrapper(v-else)
    .chart-wrapper-title {{ title }}
    apexchart(type="area", :height="350", :options="chartOptions", :series="chartData")
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import backend from "@/api";
import { handleError } from "@/services/handleErrors";
import { theme } from "@/services/useTheme";
import { parse } from "date-fns";
import { cleaningDashboardLocales } from "@/services/useLocales";

const props = defineProps({
  /* id текущей основной орагнизации выбранной пользователем для дашборда в фильтрах */
  facilityId: { type: Number, default: -1 },
  /** Фильтр даты от. Пример: "2025-10-28T21:00:00.000Z" */
  dateFrom: { type: String, default: "" },
  /** Фильтр даты до. Пример: "2025-10-29T21:00:00.000Z" */
  dateTo: { type: String, default: "" },
  /** Тип дат за которые запрашиваются данные. Пример: "today" */
  dateType: { type: String, default: "today" },
  /** Дополнительные фильтры. Пример: { building_ids: [1, 2, 3] } */
  additionalFilters: { type: Object, default: () => {} },
  /** Фильтр даты от при произвольном промежутке. Пример: "01.01.1970" */
  customDateFrom: { type: String, default: () => "" },
  /** Фильтр даты до при произвольном промежутке. Пример: "01.01.1970" */
  customDateTo: { type: String, default: () => "" },
});

const path = "/api/v3/cleaning/dashboard";

const dateType = computed(() => props.dateType);
const dateFrom = computed(() => (props.dateType === "custom" ? customDateFrom.value : props.dateFrom));
const dateTo = computed(() => (props.dateType === "custom" ? customDateTo.value : props.dateTo));
const itsOneDay = computed(() => {
  const startedAt = parse(props.customDateFrom, "dd.MM.yyyy", new Date());
  const finishedAt = parse(props.customDateTo, "dd.MM.yyyy", new Date());
  startedAt.setHours(0, 0, 0, 0);
  finishedAt.setHours(0, 0, 0, 0);
  return (
    (dateType.value === "custom" && Number(startedAt) === Number(finishedAt)) ||
    dateType.value === "today" ||
    dateType.value === "yesterday"
  );
});
const customDateFrom = computed(() => {
  if (props.customDateFrom === "") {
    return "";
  }
  const date = parse(props.customDateFrom, "dd.MM.yyyy", new Date());
  return date.toISOString();
});
const customDateTo = computed(() => {
  if (props.customDateTo === "") {
    return "";
  }
  const dateTo = parse(props.customDateTo, "dd.MM.yyyy", new Date());
  if (itsOneDay.value) {
    dateTo.setHours(24, 0, 0, 0);
  } else {
    dateTo.setHours(23, 59, 59, 999);
  }
  return dateTo.toISOString();
});
const additionalFilters = computed(() => props.additionalFilters);
const currentTheme = computed(() => theme.value);

const tickAmount = computed(() => {
  if (
    dateType.value === "today" ||
    dateType.value === "yesterday" ||
    (dateType.value === "custom" && itsOneDay.value)
  ) {
    // 13 это максимальное количество тиков за сутки, с 00:00 по 00:00 каждые 2 часа
    return 13;
  } else {
    return undefined;
  }
});
const stepSize = computed(() => {
  if (
    dateType.value === "today" ||
    dateType.value === "yesterday" ||
    (dateType.value === "custom" && itsOneDay.value)
  ) {
    return 2;
  } else {
    return undefined;
  }
});

const labelColor = computed(() => {
  return currentTheme.value === "dark" ? "#78839C" : "#78839C";
});
const markerColor = computed(() => {
  return currentTheme.value === "dark" ? "#262629" : "#ffffff";
});
const borderColor = computed(() => {
  return currentTheme.value === "dark" ? "#292D37" : "#d7dae1";
});
const showXAxisLines = computed(() => {
  return (
    dateType.value === "today" || dateType.value === "yesterday" || (dateType.value === "custom" && itsOneDay.value)
  );
});

const loading = ref(false);
const title = ref("");
const categories = ref([]);
const series = ref([]);
const canSeeTooltipOnHover = ref(false);

const chartOptions = ref({
  chart: {
    type: "area", // Тип чарта
    toolbar: {
      show: false, // Блокируем дефолтное отображение тулбара, т.к. используем кастомный
    },
    events: {
      mouseMove: (e, chartContext, config) => {
        // Задаем обработчик события движения курсора по чарту
        if (!loading.value && config.dataPointIndex !== -1) {
          // Если чарт не загружен, не делаем ничего
          // Ось x хранящая в себе тексты
          const xAxis = chartContext.el.querySelector(".apexcharts-xaxis-texts-g");
          const tooltip = chartContext.el.querySelector(".apexcharts-tooltip"); // Текущий тултип
          const currentTooltipClientRect = tooltip.getBoundingClientRect(); // DOMRect тултипа
          let barIndex = config.dataPointIndex; // Индекс массива текущего маркера
          let seriesIndex = config.seriesIndex; // Индекс массива текущей серии
          const tooltipWidth = currentTooltipClientRect?.width; // Длина тултипа
          const tooltipHeight = currentTooltipClientRect?.height; // Высота тултипа
          const currentMarker = chartContext.el
            .querySelector(`.apexcharts-series[data\\:\\realIndex="${seriesIndex}`)
            .querySelector(".apexcharts-marker");
          const markerWidth = Number(currentMarker.getAttribute("r")) * 2; // Длина текущего маркера;
          // Отступ самого чарта от левой границы экрана
          const chartOffsetX = chartContext.el.querySelector(".apexcharts-graphical").getBoundingClientRect().left;
          // Разница между отступом от левой границы экрана до чарта и левой границы экрана
          // до непосредственно самих графиков. Нужно для вычисления позиции тултипа
          const chartWrapperMargin =
            chartOffsetX - chartContext.el.querySelector(".apexcharts-canvas").getBoundingClientRect().left;
          // Расстояние слева до текущего бара
          const markerMarginLeft =
            currentMarker.getBoundingClientRect().left -
            chartContext.el.querySelector(".apexcharts-graphical").getBoundingClientRect().left;
          // Отступ тултипа сверху графика. Берется из аттрибута бара в котором лежит отступ от верха
          tooltip.style.marginTop = `${currentMarker.getAttribute("cy")}px`;
          // Отступ слева. Высчитывается по формуле суммы всех отступов + половина длины бара
          tooltip.style.marginLeft = `${chartWrapperMargin + markerMarginLeft + markerWidth / 2}px`;

          if (window.innerWidth - currentMarker.getBoundingClientRect().left < tooltipWidth) {
            tooltip.style.transform = `translate(calc(-100% + ${markerWidth + 2}px), -80%)`;
            tooltip.style.setProperty("--arrow-margin", `${tooltipWidth - markerWidth - 2}px`);
            tooltip.style.setProperty("--border-radius", "12px 12px 0px 12px");
          } else {
            // Если умещается, то выставляем тултип ровно над баров, а указывающий треугольник посередине
            tooltip.style.setProperty("--arrow-margin", `50%`);
            tooltip.style.transform = `translate(-50%, -80%)`;
            tooltip.style.setProperty("--border-radius", "12px");
          }
        }
      },
      mouseLeave: (e, chartContext) => {
        // задаем обработчик события покидания курсора с чарта
        if (!loading.value) {
          // Если чарт загружается ничего не делаем
          const tooltip = chartContext.el.querySelector(".apexcharts-tooltip"); // DOM элемент тултипа
          const xAxis = chartContext.el.querySelector(".apexcharts-xaxis-texts-g"); // DOM оси X

          // Удаляем класс с тултипа задающий ему opacity: 1 !important чтобы он исчез
          tooltip.classList.remove("always-show-tooltip");

          // Удаляем с оси X все закрашивания
          xAxis.childNodes.forEach(item => {
            item.style.fill = "var(--header-subtitle-color)";
          });
        }
      },
    },
    zoom: {
      enabled: false,
    },
  },
  stroke: {
    curve: "smooth",
    width: 2,
    colors: ["#14C783"],
  },
  colors: ["#89E3C1"],
  xaxis: {
    categories: [],
    axisTicks: {
      show: false,
    },
    tooltip: {
      enabled: false,
    },
    tickAmount: tickAmount.value,
    stepSize: stepSize.value,
    crosshairs: {
      show: false,
    },
    tooltip: {
      enabled: false,
    },
    labels: {
      style: {
        colors: labelColor.value,
      },
    },
    axisBorder: {
      show: false,
    },
  },
  yaxis: {
    show: true,
    showAlways: false,
    labels: {
      style: {
        colors: labelColor.value,
      },
    },
    crosshairs: {
      show: false,
    },
  },
  grid: {
    borderColor: borderColor.value,
    xaxis: {
      lines: {
        show: showXAxisLines.value,
      },
    },
  },
  dataLabels: {
    enabled: false,
  },
  // Задаем кастомный маркер для точек
  markers: {
    size: 0,
    colors: [markerColor.value],
    strokeWidth: 2,
    strokeColors: "#14C783",
    hover: {
      size: 4,
    },
  },
  // Тултип
  tooltip: {
    // Убираем перемещение тултипа за курсором
    followCursor: false,
    // Задаем статичную позицию тултипа слева-сверху графика, мы его перемещаем в событии mouseMove
    fixed: {
      enabled: true,
      position: "topLeft",
    },
    // Создаем кастомный тултип
    custom: function ({ series, seriesIndex, dataPointIndex, w }) {
      const date = categories.value[dataPointIndex]; // Текст категории под баром
      const color = w.config.stroke.colors[seriesIndex]; // Цвет серии
      let value = series[seriesIndex][dataPointIndex]; // Значение бара

      // Текст в тултипа
      let text = `<div class="activity-chart__tooltip"><div class='activity-chart__tooltip__header'><span class='activity-chart__tooltip__circle', style='background-color: ${color}'></span>`;
      text += `<span class='activity-chart__tooltip__label'>${date}</span></div>`;
      text += `<div class='activity-chart__tooltip__value'>${cleaningDashboardLocales.value.employees}: ${value}</div></div>`;

      return text;
    },
  },
});

const chartData = computed(() => {
  return [
    {
      name: "checkins",
      data: series.value,
    },
  ];
});

const getHourFromDate = iso => {
  const date = new Date(iso);
  return date.getHours();
};

const getDayFromDate = iso => {
  const date = new Date(iso);
  return date.getDate();
};

const getData = async () => {
  if (!loading.value) {
    try {
      loading.value = true;
      categories.value = [];
      series.value = [];

      const defaultParams = {
        facility_id: props.facilityId,
        date_from: dateFrom.value,
        date_to: dateTo.value,
      };

      const params = { ...defaultParams, ...additionalFilters.value };

      const { data } = await backend.index(`${path}/chart`, { params }, { encodeNestedData: true });

      title.value = data?.labels?.title ?? "";

      data.data.forEach(item => {
        series.value.push(item?.works_count);
        if (dateType.value === "today" || dateType.value === "yesterday") {
          categories.value.push(`${getHourFromDate(item?.bucket)}:00`);
        } else if (dateType.value === "custom") {
          if (itsOneDay.value) {
            categories.value.push(`${getHourFromDate(item?.bucket)}:00`);
          } else {
            categories.value.push(getDayFromDate(item?.bucket));
          }
        } else {
          categories.value.push(getDayFromDate(item?.bucket));
        }
      });

      // Переназначаем chartOptions на те же значения что и были, кроме измененного xaxis
      // Если не переназначать на те же что и были, а только менять chartOptions.value.xaxis, apexcharts не перерисовывает график
      chartOptions.value = {
        ...chartOptions.value,
        xaxis: {
          categories: categories.value,
          axisTicks: {
            show: false,
          },
          tooltip: {
            enabled: false,
          },
          tickAmount: tickAmount.value,
          stepSize: stepSize.value,
          crosshairs: {
            show: false,
          },
          tooltip: {
            enabled: false,
          },
          labels: {
            style: {
              colors: labelColor.value,
            },
          },
          axisBorder: {
            show: false,
          },
        },
        yaxis: {
          show: true,
          showAlways: false,
          labels: {
            style: {
              colors: labelColor.value,
            },
          },
          crosshairs: {
            show: false,
          },
        },
        grid: {
          borderColor: borderColor.value,
          xaxis: {
            lines: {
              show: showXAxisLines.value,
            },
          },
        },
        markers: {
          size: 0,
          colors: [markerColor.value],
          strokeWidth: 2,
          strokeColors: "#14C783",
          hover: {
            size: 4,
          },
        },
      };
    } catch (err) {
      await handleError(err, { notify: false });
    } finally {
      loading.value = false;
    }
  }
};

const refreshChart = async () => {
  await getData();
};

watch(currentTheme, async (newVal, oldVal) => {
  if (newVal !== oldVal) {
    await refreshChart();
  }
});

onMounted(async () => {
  await getData();
});

defineExpose({ getData });
</script>

<style lang="scss" scoped>
.chart {
  background-color: var(--cleaning-dashboard-block-background);
  padding: 24px;
  border-radius: 20px;
  border: 1px solid var(--cleaning-dashboard-block-border);
  font-size: 20px;
  position: relative;

  &-wrapper {
    margin: 0;

    &-title {
      color: var(--cleaning-dashboard-text-color);
    }
  }

  &-loader {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 400px;
  }
}
</style>

<style lang="scss">
.cleaning__wrapper-chart {
  .apexcharts-xaxistooltip.apexcharts-active {
    display: none;
  }

  .apexcharts-tooltip {
    box-shadow: 0px 0px 24px 0px #2c3f581f;
    --arrow-margin: 50%;
    --border-radius: 12px;
    border: none !important;
    border-radius: var(--border-radius) !important;
    background-color: transparent !important;
    overflow: visible;

    .activity-chart__tooltip {
      position: relative;
      display: flex;
      width: 180px;
      height: 60px;
      justify-content: center;
      align-items: center;
      flex-flow: column nowrap;
      border-radius: var(--border-radius) !important;
      background-color: var(--dashboard-card-background);

      &::after {
        content: "";
        width: 20px;
        height: 20px;
        display: block;
        z-index: 1;
        position: absolute;
        border: 11px solid transparent;
        border-top: 11px solid var(--dashboard-card-background);
        bottom: -22px;
        left: var(--arrow-margin);
        transform: translateX(-50%);
      }

      &__circle {
        display: block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 6px;
      }

      &__header {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        color: var(--cleaning-dashboard-text-color);
      }

      &__value {
        width: 100%;
        display: flex;
        justify-content: center;
        color: var(--cleaning-dashboard-text-color);
      }
    }
  }
}
</style>
